import { GetStaticProps, NextPage } from 'next';
import { MagazinePage } from 'pages/Magazine/MagazinePage';
import { Magazine } from 'interfaces/Taxonomy';
import getMagazinesByPage from 'api/Magazine/getMagazinesByPage';
import { HeaderNav } from 'interfaces/navigation/Header';
import { PageViewDataLayerObject } from 'services/Gtm/entities';
import setPageGtmConfig from 'services/Gtm/setPageGtmConfig';
import { layoutCount } from 'constants/constants';
import { PageInfo } from 'interfaces/components/PageInfo';
import { ArcSpanPageConfig } from 'services/ArcSpan/entities';
import { setarcSpanPageConfig } from 'services/ArcSpan';
import setMagazinePageAdConfig from 'services/Ad/config/setMagazinePageAdConfig';
import { PageAdConfig } from 'interfaces/ads/Ad';
import { ArticlePreview } from 'interfaces/content/articles/Articles';
import getMostPopularPosts from 'api/MostPopular/getMostPopular';
import { PianoPageConfig } from 'services/Piano/entities/PianoPageConfig';
import { setPianoConfig } from 'services/Piano';
import { HeadData, setHeadData } from 'utils/metaDataUtils/headData';
import getFooter from 'api/Footer/getFooter';
import handleStaticPropsError from 'utils/handleStaticPropsError';
import getMenu from 'api/Header/getMenu';
import logApiError from 'utils/log/logApiError';
import { safe, safeAsync } from 'utils/errorHandlingUtils';
import fallbackData from 'lib/fallbackData/commonData';

interface MagazinesProps {
  magazines: Magazine[];
  mostPopular: ArticlePreview[];
  magazinePageInfo: PageInfo;
  pageSize: number;
}

const MagazinesNextPage: NextPage<MagazinesProps> = ({
  magazines,
  mostPopular,
  magazinePageInfo,
  pageSize,
}: MagazinesProps) => (
  <MagazinePage
    magazines={magazines}
    magazinePageInfo={magazinePageInfo}
    mostPopular={mostPopular}
    pageNum={1}
    pageSize={pageSize}
  />
);

export default MagazinesNextPage;

export const getStaticProps: GetStaticProps = async () => {
  const pageSize = layoutCount.magazine;

  try {
    const { magazinesPage, magazinePageInfo } = await getMagazinesByPage(pageSize, 0);
    const menu: HeaderNav = await safeAsync(
      () => getMenu('LEFT_MENU_COM', 'RIGHT_MENU_COM', 'TOPIC_NAVIGATION_COM', 'own', null),
      fallbackData.headerNav,
    );
    const footer = await safeAsync(() => getFooter(), fallbackData.footer);

    const mostPopular = await getMostPopularPosts().catch((error) => {
      logApiError(error as Error, '[pages][magazine]');
      return [];
    });

    const dataLayer: PageViewDataLayerObject = safe(() => setPageGtmConfig(magazinesPage), fallbackData.dataLayer);
    const headData: HeadData = safe(() => setHeadData(magazinesPage), fallbackData.headData);
    const arcSpanPageConfig: ArcSpanPageConfig = safe(
      () => setarcSpanPageConfig(magazinesPage),
      fallbackData.arcSpanPageConfig,
    );
    const pianoPageConfig: PianoPageConfig = safe(() => setPianoConfig(magazinesPage), fallbackData.pianoPageConfig);
    const pageAdConfig: PageAdConfig = safe(() => setMagazinePageAdConfig(magazinesPage), fallbackData.pageAdConfig);

    return {
      props: {
        arcSpanPageConfig,
        dataLayer,
        footer,
        headData,
        headerNav: menu,
        magazinePageInfo,
        magazines: magazinesPage.magazines,
        mostPopular,
        pageAdConfig,
        pageSize,
        pianoPageConfig,
      },
      revalidate: 60,
    };
  } catch (e) {
    return handleStaticPropsError(e as Error);
  }
};
